import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/Label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup'
import { toast } from 'sonner'
import {
  ACCOUNTING_PROVIDERS,
  type AccountingProvider,
} from '@/services/api/integrations/accountingProviders'
import {
  clearAccountingConnectTeam,
  useCompleteAccountingConnect,
  useSelectAccountingOrganisation,
  type AccountingCallbackResult,
  type AccountingOrganisation,
} from '@/services/api/integrations/useAccountingConnection'

const INTEGRATIONS_URL = '/account?section=integrations'

/**
 * Redirect target registered with the accounting provider for the Space
 * connection. Hands code + state to the API (which holds the client secret),
 * then either finishes or asks the admin to pick one organisation.
 */
export default function AccountingConnectCallbackPage({
  provider,
}: {
  provider: AccountingProvider
}) {
  const { label, organisation } = ACCOUNTING_PROVIDERS[provider]
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const started = useRef(false)

  const { action: complete } = useCompleteAccountingConnect(provider)
  const { action: selectOrganisation, status: selectStatus } =
    useSelectAccountingOrganisation(provider)

  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<{
    pendingId: string
    organisations: AccountingOrganisation[]
  } | null>(null)
  const [tenantId, setTenantId] = useState<string>('')

  const finish = (result: AccountingCallbackResult) => {
    if (result.status === 'connected') {
      clearAccountingConnectTeam(provider)
      toast.success(
        `Connected to ${result.connection.tenant_name ?? `your ${organisation}`}`,
      )
      navigate(INTEGRATIONS_URL, { replace: true })
      return
    }
    setPending({
      pendingId: result.pending_id,
      organisations: result.organisations,
    })
    setTenantId(result.organisations[0]?.tenant_id ?? '')
  }

  useEffect(() => {
    // Codes are single-use; guard against StrictMode's double effect.
    if (started.current) return
    started.current = true

    const providerError = searchParams.get('error')
    const code = searchParams.get('code')
    const state = searchParams.get('state')

    if (providerError || !code || !state) {
      clearAccountingConnectTeam(provider)
      setError(
        providerError === 'access_denied'
          ? `${label} connection was cancelled.`
          : `${label} did not complete the connection. Please try again.`,
      )
      return
    }

    // Zoho names the account's data centre; the API maps it to its own list of hosts.
    complete({ code, state, location: searchParams.get('location') })
      .then(finish)
      .catch((e) => {
        clearAccountingConnectTeam(provider)
        setError(
          e?.response?.data?.message ??
            `We could not connect to ${label}. Please try again.`,
        )
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSelect = async () => {
    if (!pending || !tenantId) return
    try {
      finish(
        await selectOrganisation({ pendingId: pending.pendingId, tenantId }),
      )
    } catch (e: any) {
      if (e?.response?.data?.code === 'invalid_state') {
        clearAccountingConnectTeam(provider)
        setPending(null)
        setError(e.response.data.message)
      }
    }
  }

  return (
    <div className="pf-auth grid min-h-screen px-4">
      <Card className="w-full max-w-md place-self-center">
        <CardHeader>
          <CardTitle>
            {pending ? `Choose a ${organisation}` : `Connecting to ${label}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button
                onClick={() => navigate(INTEGRATIONS_URL, { replace: true })}
              >
                Back to Integrations
              </Button>
            </>
          ) : pending ? (
            <>
              <p className="text-sm text-muted-foreground">
                {provider === 'xero'
                  ? 'You authorised several organisations.'
                  : `Your ${label} login has several organisations.`}{' '}
                A Space can be connected to one {organisation}.
              </p>
              <RadioGroup value={tenantId} onValueChange={setTenantId}>
                {pending.organisations.map((org) => (
                  <div key={org.tenant_id} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={org.tenant_id}
                      id={`org-${org.tenant_id}`}
                    />
                    <Label htmlFor={`org-${org.tenant_id}`}>
                      {org.tenant_name ?? org.tenant_id}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <Button
                onClick={handleSelect}
                disabled={!tenantId || selectStatus.isPending}
                loading={selectStatus.isPending}
              >
                Connect organisation
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Finishing the connection…
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
